import { Table } from 'antd'
import { observer } from 'mobx-react-lite'

import { classNames } from '@/Ship/Helpers/classNames'

import { LOGISTICIANS_FINANCIAL_SORT_FIELDS } from '../Contracts/ILogisticiansFinancialReport'
import { formatReportCount, formatReportMoney, formatReportPercent } from '../Helpers/reportFormat'
import { NUMERIC_SORT_DIRECTIONS, sortFromTable, sortOrderOf } from '../Helpers/reportSort'
import { logisticiansFinancialReportStore as store } from '../Stores/ReportStore'
import { ReportUserCell } from './ReportUserCell'

import type { ILogisticiansFinancialReport, ILogisticiansFinancialRow } from '../Contracts/ILogisticiansFinancialReport'
import type { TableColumnsType } from 'antd'

import styles from './Report.module.css'

/**
 * Таблиця «Звіт по логістам - фінансовий» (`SPEC-027` §4.2): колонки й
 * підписи легасі (Ф-09), сортування — бек (К-06), «Разом» — з бека (Ф-06,
 * Q3.8). Без пагінації: рядок — співробітник із заявками в періоді.
 */
export const LogisticiansFinancialTable = observer(({ report }: { report: ILogisticiansFinancialReport }) => {
    const columns: TableColumnsType<ILogisticiansFinancialRow> = [
        {
            key: 'user',
            title: 'Логіст',
            render: (_: unknown, row) => <ReportUserCell user={row.user} />,
        },
        {
            key: 'applications',
            title: 'К-сть заявок',
            align: 'right',
            sorter: true,
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            sortOrder: sortOrderOf(store.sort, 'applications'),
            render: (_: unknown, row) => formatReportCount(row.applications),
        },
        {
            key: 'profit',
            title: 'Загальна сума',
            align: 'right',
            sorter: true,
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            sortOrder: sortOrderOf(store.sort, 'profit'),
            render: (_: unknown, row) => formatReportMoney(row.profit),
        },
        {
            key: 'percentage',
            title: 'Відсоток',
            align: 'right',
            sorter: true,
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            sortOrder: sortOrderOf(store.sort, 'percentage'),
            render: (_: unknown, row) => formatReportPercent(row.percentage),
        },
    ]

    return (
        <Table<ILogisticiansFinancialRow>
            data-testid="report-financial-table"
            rowKey={(row) => row.user.uuid}
            columns={columns}
            dataSource={report.rows}
            pagination={false}
            loading={store.isLoading}
            scroll={{ x: 'max-content' }}
            showSorterTooltip={false}
            onChange={(_pagination, _filters, sorter) => {
                const single = Array.isArray(sorter) ? sorter[0] : sorter

                void store.setSort(sortFromTable(LOGISTICIANS_FINANCIAL_SORT_FIELDS, single?.columnKey, single?.order))
            }}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row className={classNames(styles['totalRow'])} data-testid="report-total">
                        <Table.Summary.Cell index={0}>Разом</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            {formatReportCount(report.total.applications)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                            {formatReportMoney(report.total.profit)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                            {formatReportPercent(report.total.percentage)}
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
})
